import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

const WS_CLIENT_HTML = `<!-- Save as: client.html (in same folder as server.mjs) -->
<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>onefold WebSocket Chat</title></head>
<body>
<div id="app"></div>
<script type="module">
import { createSignal, createWebSocket, html, mount } from 'https://esm.sh/onefold@latest';

const chat = createWebSocket('ws://localhost:3000');

function App() {
  const input = createSignal('');
  const name = createSignal('User');

  const send = () => {
    const text = input().trim();
    if (!text) return;
    chat.send({ user: name(), text });
    input.set('');
  };

  return html\`
    <div style="max-width:500px;margin:20px auto;font-family:sans-serif">
      <h2>Chat (\${() => chat.status()})</h2>
      <div style="height:300px;overflow-y:auto;border:1px solid #ddd;padding:10px;margin-bottom:10px;border-radius:8px">
        \${() => chat.data().map(m => html\`
          <div style="margin-bottom:6px"><b>\${m.user}</b>: \${m.text}</div>
        \`)}
      </div>
      <div style="display:flex;gap:8px">
        <input value=\${() => name()} oninput=\${(e) => name.set(e.target.value)} placeholder="Name" style="width:80px;padding:6px" />
        <input value=\${() => input()} oninput=\${(e) => input.set(e.target.value)} onkeydown=\${(e) => { if(e.key==='Enter') send(); }} placeholder="Message..." style="flex:1;padding:6px" />
        <button onclick=\${send} style="padding:6px 12px">Send</button>
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));
<\/script>
</body></html>`;

export function WebSocketPage(): Node {
  return html`
    <div>
      <h1>WebSocket</h1>
      <p><code>createWebSocket</code> wraps the native WebSocket API in reactive signals with auto-reconnect, typed messages, and connection state tracking.</p>

      <h2>Client Usage</h2>
      ${CodeBlock(`import { html, mount } from 'onefold';
import { createWebSocket } from 'onefold/stream';

interface ChatMessage {
  user: string;
  text: string;
  ts: number;
}

const chat = createWebSocket<ChatMessage>('ws://localhost:3000/ws');

// Reactive signals — UI updates automatically when messages arrive
chat.data()      // Signal<ChatMessage[]> — all received messages
chat.latest()    // Signal<ChatMessage | null> — most recent message
chat.status()    // Signal<'connecting' | 'open' | 'closed' | 'error'>

// Send a message (serialized to JSON automatically)
chat.send({ user: 'Alice', text: 'Hello!' });

// Close / reconnect
chat.close();
chat.reconnect();`)}

      <h2>Chat App Example</h2>
      ${CodeBlock(`import { createSignal, html, mount } from 'onefold';
import { createWebSocket } from 'onefold/stream';

interface ChatMessage { user: string; text: string; ts: number; }

const chat = createWebSocket<ChatMessage>('ws://localhost:3000/ws');

function ChatApp() {
  const input = createSignal('');
  const username = createSignal('Anonymous');

  const send = () => {
    const text = input().trim();
    if (!text) return;
    chat.send({ user: username(), text, ts: Date.now() });
    input.set('');
  };

  return html\`
    <div>
      <h2>Chat (\${() => chat.status()})</h2>
      <div style="height:250px;overflow-y:auto;border:1px solid #e5e7eb;padding:8px;border-radius:8px;margin-bottom:12px">
        \${() => chat.data().map(msg => html\`
          <div style="margin-bottom:6px">
            <strong>\${msg.user}</strong>: \${msg.text}
            <small style="color:#999;margin-left:8px">\${new Date(msg.ts).toLocaleTimeString()}</small>
          </div>
        \`)}
      </div>
      <div style="display:flex;gap:8px">
        <input placeholder="Name" value=\${() => username()} oninput=\${(e) => username.set(e.target.value)} style="width:100px" />
        <input placeholder="Message..." value=\${() => input()} oninput=\${(e) => input.set(e.target.value)} onkeydown=\${(e) => { if (e.key === 'Enter') send(); }} style="flex:1" />
        <button onclick=\${send}>Send</button>
      </div>
    </div>
  \`;
}

mount(ChatApp(), document.getElementById('app')!);`)}

      <h2>Server (Node.js)</h2>
      <p>A simple WebSocket server using the <code>ws</code> package:</p>
      ${CodeBlock(`// server.mjs
import { WebSocketServer } from 'ws';
import http from 'node:http';

const server = http.createServer();
const wss = new WebSocketServer({ server });

const clients = new Set();

wss.on('connection', (ws) => {
  clients.add(ws);

  // Send welcome
  ws.send(JSON.stringify({ user: 'System', text: 'Welcome!', ts: Date.now() }));

  // Broadcast incoming messages to all clients
  ws.on('message', (raw) => {
    const msg = JSON.parse(raw.toString());
    msg.ts = Date.now();
    for (const client of clients) {
      if (client.readyState === 1) { // OPEN
        client.send(JSON.stringify(msg));
      }
    }
  });

  ws.on('close', () => clients.delete(ws));
});

server.listen(3000, () => console.log('WebSocket server on :3000'));`)}

      <h2>Authentication</h2>
      <p>WebSocket doesn't send custom headers during the initial handshake in browsers. Common auth patterns:</p>

      <h3>Option 1: Token in URL (simplest)</h3>
      ${CodeBlock(`// Client — pass token in query string
const token = getAuthToken();
const chat = createWebSocket<Msg>(\`ws://localhost:3000/ws?token=\${token}\`);

// Server — validate on connection
wss.on('connection', (ws, req) => {
  const url = new URL(req.url, 'http://localhost');
  const token = url.searchParams.get('token');

  if (!token || !verifyToken(token)) {
    ws.close(4001, 'Unauthorized');
    return;
  }

  // Token valid — proceed
  const user = decodeToken(token);
  ws.userId = user.id;
});`)}

      ${Callout('Token in URL is visible in server logs and browser history. Use short-lived tokens (e.g., 30-second JWTs) that are exchanged for the WebSocket session.', 'warn')}

      <h3>Option 2: First-message auth (more secure)</h3>
      ${CodeBlock(`// Client — send auth as first message after connection
const chat = createWebSocket<Msg>('ws://localhost:3000/ws');

// Wait for connection, then authenticate
createEffect(() => {
  if (chat.status() === 'open') {
    chat.send({ type: 'auth', token: getAuthToken() });
  }
});

// Server — require auth before accepting messages
wss.on('connection', (ws) => {
  let authenticated = false;

  ws.on('message', (raw) => {
    const msg = JSON.parse(raw.toString());

    if (!authenticated) {
      if (msg.type === 'auth' && verifyToken(msg.token)) {
        authenticated = true;
        ws.send(JSON.stringify({ type: 'auth_ok' }));
      } else {
        ws.close(4001, 'Unauthorized');
      }
      return;
    }

    // Normal message handling (only after auth)
    broadcast(msg);
  });

  // Disconnect if not authenticated within 5 seconds
  setTimeout(() => {
    if (!authenticated) ws.close(4001, 'Auth timeout');
  }, 5000);
});`)}

      <h3>Option 3: Cookie-based (for same-origin)</h3>
      ${CodeBlock(`// Client — no extra code needed (browser sends cookies automatically)
const chat = createWebSocket<Msg>('ws://localhost:3000/ws');

// Server — read cookie from upgrade request
wss.on('connection', (ws, req) => {
  const cookies = parseCookies(req.headers.cookie);
  const sessionId = cookies['session_id'];

  if (!sessionId || !validateSession(sessionId)) {
    ws.close(4001, 'Unauthorized');
    return;
  }

  const user = getSessionUser(sessionId);
  ws.userId = user.id;
});`)}

      <h2>Authorization (room-based)</h2>
      ${CodeBlock(`// Server — check permissions per action
ws.on('message', (raw) => {
  const msg = JSON.parse(raw.toString());

  switch (msg.type) {
    case 'join_room':
      if (!userCanAccessRoom(ws.userId, msg.roomId)) {
        ws.send(JSON.stringify({ type: 'error', message: 'Access denied' }));
        return;
      }
      rooms.get(msg.roomId)?.add(ws);
      break;

    case 'send_message':
      if (!userCanWrite(ws.userId, msg.roomId)) {
        ws.send(JSON.stringify({ type: 'error', message: 'Read-only access' }));
        return;
      }
      broadcastToRoom(msg.roomId, msg);
      break;
  }
});`)}

      <h2>Options</h2>
      <table>
        <tr><th>Option</th><th>Type</th><th>Default</th><th>Description</th></tr>
        <tr><td><code>maxMessages</code></td><td>number</td><td>100</td><td>Max messages kept in data() array</td></tr>
        <tr><td><code>autoReconnect</code></td><td>boolean</td><td>true</td><td>Auto-reconnect on disconnect</td></tr>
        <tr><td><code>reconnectDelay</code></td><td>number</td><td>3000</td><td>Delay before reconnecting (ms)</td></tr>
        <tr><td><code>maxRetries</code></td><td>number</td><td>5</td><td>Max reconnect attempts</td></tr>
        <tr><td><code>parse</code></td><td>function</td><td>JSON.parse</td><td>Custom message parser</td></tr>
      </table>

      <h2>Try It (Simulated WebSocket)</h2>
      <p>This playground simulates a WebSocket connection so you can see how the reactive signals update the UI. Run the server code above locally to connect for real.</p>

      ${Playground(`import { createSignal, html, mount } from 'onefold';

// Simulated WebSocket — demonstrates the reactive API pattern
// For a real app, use: createWebSocket('ws://localhost:3000/ws')

const messages = createSignal([]);
const status = createSignal('connecting');
const input = createSignal('');
const username = createSignal('You');

// Simulate connection after 500ms
setTimeout(() => {
  status.set('open');
  messages.set(prev => [...prev, { user: 'System', text: 'Connected!', ts: Date.now() }]);
}, 500);

// Simulate receiving messages every 3s
setInterval(() => {
  if (status() !== 'open') return;
  const bots = ['Alice', 'Bob', 'Charlie'];
  const texts = ['Hey there!', 'How is it going?', 'onefold is great!', 'Anyone here?'];
  messages.set(prev => [...prev, {
    user: bots[Math.floor(Math.random() * bots.length)],
    text: texts[Math.floor(Math.random() * texts.length)],
    ts: Date.now()
  }]);
}, 3000);

function sendMessage() {
  const text = input().trim();
  if (!text || status() !== 'open') return;
  messages.set(prev => [...prev, { user: username(), text, ts: Date.now() }]);
  input.set('');
  const el = document.getElementById('msg-input');
  if (el) el.value = '';
}

function App() {
  return html\`
    <div>
      <h3>Chat (\${() => status()})</h3>
      <div style="height:150px;overflow-y:auto;border:1px solid #e5e7eb;padding:8px;border-radius:6px;margin-bottom:8px;font-size:13px">
        \${() => messages().map(m => html\`
          <div style="margin-bottom:4px">
            <strong>\${m.user}</strong>: \${m.text}
            <small style="color:#999;margin-left:6px">\${new Date(m.ts).toLocaleTimeString()}</small>
          </div>
        \`)}
      </div>
      <div style="display:flex;gap:6px">
        <input id="msg-input" placeholder="Message..." oninput=\${(e) => input.set(e.target.value)} onkeydown=\${(e) => { if (e.key === 'Enter') sendMessage(); }} style="flex:1" />
        <button onclick=\${sendMessage}>Send</button>
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'WebSocket Chat (Simulated)')}

      <div class="callout">
        <p><strong>To connect to a real server:</strong> Replace the simulated signals above with <code>createWebSocket('ws://localhost:3000/ws')</code> and run the Node.js server code shown earlier. The reactive API (<code>.data()</code>, <code>.latest()</code>, <code>.status()</code>, <code>.send()</code>) works identically.</p>
      </div>

      <h2>Run Locally (Copy and Paste)</h2>
      <p>Save these two files, run the server, then open the client in a browser:</p>

      <h3>Step 1: server.mjs</h3>
      ${CodeBlock(`// Save as: server.mjs
// Run: npm install ws && node server.mjs
import { WebSocketServer } from 'ws';
import http from 'node:http';
import { readFileSync } from 'node:fs';

const server = http.createServer((req, res) => {
  if (req.url === '/') {
    res.writeHead(200, { 'Content-Type': 'text/html' });
    res.end(readFileSync('client.html'));
    return;
  }
  res.writeHead(404); res.end();
});

const wss = new WebSocketServer({ server });
const clients = new Set();

wss.on('connection', (ws) => {
  clients.add(ws);
  ws.send(JSON.stringify({ user: 'System', text: 'Welcome! ' + clients.size + ' online', ts: Date.now() }));

  ws.on('message', (raw) => {
    const msg = JSON.parse(raw.toString());
    msg.ts = Date.now();
    for (const c of clients) {
      if (c.readyState === 1) c.send(JSON.stringify(msg));
    }
  });

  ws.on('close', () => clients.delete(ws));
});

server.listen(3000, () => console.log('Chat server: http://localhost:3000'));`)}

      <h3>Step 2: client.html</h3>
      ${CodeBlock(WS_CLIENT_HTML)}

      <h3>Step 3: Run</h3>
      ${CodeBlock(`npm install ws
node server.mjs
# Open http://localhost:3000 in multiple tabs to chat between them`)}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/streaming/sse">Server-Sent Events</a> — one-way server push (simpler, works through proxies)</li>
        <li><a href="/data/http-client">HTTP Client</a> — for request/response communication</li>
        <li><a href="/security/guards">RBAC Guards</a> — client-side route protection</li>
      </ul>
    </div>
  `;
}
